import 'css/src/components/common/AnnouncementPopup';

import React from 'react';
import { useSelector } from 'react-redux';

import { AnnouncementCard } from 'js/src/components/common/AnnouncementCard';
import {
    loadAnnouncements,
    markAnnouncementAsViewed,
    selectAllAnnouncements,
} from 'js/src/data/announcements';
import type { Announcement } from 'js/src/data/announcements/announcement-types';
import type { ImmutableMap } from 'js/src/libs/consts';
import { useAppDispatch } from 'js/src/libs/RootState';

/**
 * Modal popup for announcements with show_popup_now=true.
 * Backend evaluates targeting (URL, plan) and sets show_popup_now accordingly.
 * Shows when user lands on a page, can be dismissed.
 */
export const AnnouncementPopup: React.FC = () => {
    const dispatch = useAppDispatch();
    const allAnnouncements = useSelector(selectAllAnnouncements);
    // Local state for preview announcements (backend doesn't persist their view status)
    const [dismissedPreviewIds, setDismissedPreviewIds] = React.useState<Set<number>>(new Set());
    const [loaded, setLoaded] = React.useState(false);

    React.useEffect(() => {
        if (!loaded && SERVER.USER) {
            dispatch(loadAnnouncements({ currentUrl: window.location.pathname }));
            setLoaded(true);
        }
    }, [dispatch, loaded]);

    const popupAnnouncements = React.useMemo(() => {
        return allAnnouncements.filter((a: ImmutableMap<Announcement>) => {
            // show_popup_now is computed by backend considering targeting rules
            if (!a.get('show_popup_now')) return false;
            if (a.get('is_expired')) return false;

            const id = a.get('id');
            const isPreview = a.get('is_preview');

            // Preview announcements: check local state (backend doesn't track views)
            if (isPreview) {
                return !dismissedPreviewIds.has(id);
            }

            // Regular announcements: check is_viewed from API/Redux
            return !a.get('is_viewed');
        });
    }, [allAnnouncements, dismissedPreviewIds]);

    const handleDismiss = React.useCallback(() => {
        if (popupAnnouncements.size > 0) {
            const currentAnnouncement = popupAnnouncements.first();
            const currentId = currentAnnouncement?.get('id');
            const isPreview = currentAnnouncement?.get('is_preview');

            if (currentId) {
                if (isPreview) {
                    setDismissedPreviewIds((prev) => new Set([...prev, currentId]));
                } else {
                    dispatch(markAnnouncementAsViewed(currentId));
                }
            }
        }
    }, [dispatch, popupAnnouncements]);

    const handleBackdropClick = React.useCallback(
        (e: React.MouseEvent) => {
            if (e.target === e.currentTarget) {
                handleDismiss();
            }
        },
        [handleDismiss],
    );

    if (popupAnnouncements.size === 0) {
        return null;
    }

    const announcement = popupAnnouncements.first()!;

    return (
        <div className="announcement-popup__backdrop" onClick={handleBackdropClick}>
            <div className="announcement-popup__modal">
                <button
                    type="button"
                    className="announcement-popup__close"
                    onClick={handleDismiss}
                    aria-label="Close"
                >
                    <i className="material-symbols-outlined">close</i>
                </button>
                <AnnouncementCard
                    announcement={announcement}
                    onCtaClick={handleDismiss}
                    titleTag="h2"
                />
            </div>
        </div>
    );
};

export default AnnouncementPopup;
